
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  SettingsCardInfo,
  SettingsCardSections,
  SettingsGetters,
  SettingsMutations
} from "@/components/settings/types";
import { Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { scrollToPage } from "@/helpers/utils";
import { UserInfo, UserRole } from "@/services/apiService/api.types";
import apiService from "@/services/apiService/apiService";
import {
  LocaleSettingsCardsProfile,
  LocaleSettingsConfirmModal
} from "@/plugins/i18n/types";
import { i18n } from "@/plugins/i18n/vue-i18n-plugin";

@Component({})
export default class SettingsCardProfile extends Vue {
  @Prop() readonly settingsCardInfo!: SettingsCardInfo;
  @Prop() readonly cardInfoIndex!: number;
  @Prop() readonly cardSection!: SettingsCardSections;
  @Prop() readonly userInfo!: UserInfo;

  @Mutation(SettingsMutations.setPinOverlay, { namespace: Namespace.Settings })
  setPinOverlay!: (overlayStatus: boolean) => void;

  @Mutation(SettingsMutations.setActiveCardIndex, {
    namespace: Namespace.Settings
  })
  setActiveCardIndex!: (cardIndex?: number) => void;

  @Mutation(SettingsMutations.setActiveCardSection, {
    namespace: Namespace.Settings
  })
  setActiveCardSection!: (cardSection?: SettingsCardSections) => void;

  @Mutation(SettingsMutations.setUserToEdit, {
    namespace: Namespace.Settings
  })
  setUserToEdit!: (userData?: UserInfo) => void;

  @Mutation(SettingsMutations.setOpenConfirmModal, {
    namespace: Namespace.Settings
  })
  setOpenConfirmModal!: (status: boolean) => void;

  @Mutation(SettingsMutations.setConfirmModalMsg, {
    namespace: Namespace.Settings
  })
  setConfirmModalMsg!: (message: string) => void;

  @Getter(SettingsGetters.getEncodedPin, { namespace: Namespace.Settings })
  getEncodedPin!: string;

  @Getter(SettingsGetters.getPinOverlay, { namespace: Namespace.Settings })
  getPinOverlay!: boolean;

  @Getter(SettingsGetters.getActiveCardIndex, { namespace: Namespace.Settings })
  getActiveCardIndex?: number;

  @Getter(SettingsGetters.getActiveCardSection, {
    namespace: Namespace.Settings
  })
  getActiveCardSection?: SettingsCardSections;

  @Getter(SettingsGetters.getOpenConfirmModal, {
    namespace: Namespace.Settings
  })
  getOpenConfirmModal!: boolean;

  @Getter(SettingsGetters.getConfirmModalStatus, {
    namespace: Namespace.Settings
  })
  getConfirmModalStatus!: boolean;

  scrollFuncRef = scrollToPage;
  showControls = false;
  focusedControl = 0;
  userRole = UserRole;

  LocaleSettingsCardsProfile = LocaleSettingsCardsProfile;

  get userInitials(): string {
    return this.userInfo.profile.first_name
      .charAt(0)
      .concat(this.userInfo.profile.last_name.charAt(0))
      .toUpperCase();
  }

  get editIcon(): string {
    return require("/src/assets/svg/svg-settings/11-edit.svg");
  }
  get deleteIcon(): string {
    return require("/src/assets/svg/svg-settings/12-delete.svg");
  }

  mounted(): void {
    this.refocusCard();
  }

  handleCardControls(event: KeyboardEvent): void {
    SpatialNavigation.pause();
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_ENTER:
        this.setActiveCardIndex(this.cardInfoIndex);
        this.setActiveCardSection(this.cardSection);
        if (!this.showControls) {
          this.showControls = true;
          return;
        }
        switch (this.focusedControl) {
          case 0:
            this.openPage();
            SpatialNavigation.resume();
            break;
          case 1:
            this.setOpenConfirmModal(true);
            this.setConfirmModalMsg(
              i18n.t(LocaleSettingsConfirmModal.userDelete) as string
            );
            SpatialNavigation.resume();
            break;
        }
        break;
      case KeySet.KEY_RIGHT:
        if (!this.showControls) {
          SpatialNavigation.resume();
          return;
        }
        if (this.focusedControl < 1 && this.cardInfoIndex !== 0) {
          this.focusedControl++;
        }
        break;
      case KeySet.KEY_LEFT:
        if (!this.showControls) {
          SpatialNavigation.resume();

          if (this.cardInfoIndex === 0) {
            SpatialNavigation.focus("#" + this.$route.name);
          }
          return;
        }
        if (this.focusedControl > 0) {
          this.focusedControl--;
        }
        break;
      case KeySet.KEY_BACK:
        this.showControls = false;
        SpatialNavigation.resume();
        break;
      default:
        SpatialNavigation.resume();
        break;
    }
  }

  //setting proper section and card id for focusing after closing overlays or returning form a sub page
  openPage(): void {
    if (
      this.getActiveCardIndex === this.cardInfoIndex &&
      this.getActiveCardSection === this.cardSection
    ) {
      if (this.settingsCardInfo.requiresPin && this.getEncodedPin === "") {
        this.setPinOverlay(true);
        return;
      }
      if (this.settingsCardInfo.attachedRoute) {
        this.setUserToEdit(this.userInfo);
        this.$router.push({ name: this.settingsCardInfo.attachedRoute });
      }
    }
  }

  refocusCard(): void {
    if (
      this.getActiveCardIndex === this.cardInfoIndex &&
      this.getActiveCardSection === this.cardSection
    ) {
      SpatialNavigation.focus("#" + this.cardSection + this.cardInfoIndex);
      this.setActiveCardIndex();
      this.setActiveCardSection();
    }
  }

  handleDelete(): void {
    this.cardInfoIndex !== 0
      ? (this.focusedControl = 1)
      : this.focusedControl === 0;
    if (this.focusedControl === 1) {
      this.setOpenConfirmModal(true);
      this.setConfirmModalMsg(
        i18n.t(LocaleSettingsConfirmModal.userDelete) as string
      );
    }
  }

  @Watch("getEncodedPin")
  handlePinValidation(newVal: string): void {
    if (newVal !== "") {
      if (!this.getConfirmModalStatus) {
        this.openPage();

        return;
      }

      if (this.getActiveCardIndex === this.cardInfoIndex) {
        apiService.deleteChildUser(this.userInfo.id, this.getEncodedPin);
        this.$emit("childDeleted");
      }
    }
  }

  @Watch("getPinOverlay")
  handlePinOverlayClose(newVal: boolean): void {
    if (!newVal) {
      this.refocusCard();
    }
  }

  @Watch("getOpenConfirmModal")
  handleConfirmOverlayClose(newVal: boolean): void {
    if (!newVal) {
      if (!this.getConfirmModalStatus) {
        this.refocusCard();

        return;
      }

      this.setPinOverlay(true);
    }
  }
}
